import produce from "immer";
import { EMPTY_TRANSLATIONS } from "src/globalConsts";
import { MobileRowLayoutDTO, TypeEnum } from "src/requests/templates/types";
import getShortUuid from "src/utils/nanoid";
import { Translations } from "src/components/molecules/modals/TranslationsModal/types";
import {
  AllDataTemplates,
  AllPresentationTemplateItems,
  AllPresentationTemplates,
  TemplateSystem,
} from "../../../requests/templateBuilder/types";

import {
  FunctionTemplate,
  NumberIndexedDictionary,
  SelectItem,
  TemplateType,
} from "../../../globalTypes";
import { FormatMsg } from "../../../hooks/useFormatMsg";
import {
  TableColumnInputType,
  TableColumns,
} from "../../../requests/table/types";
import { presentationTemplateItemPLACEHOLDER } from "../../../requests/templateBuilder/types/placeholderData";
import { columnFormatPLACEHOLDER } from "../../../requests/templates/types/placeholderData";
import typeGuards from "../../../type-guards";
import { PresentationTemplateGroups } from "../../../type-guards/modelGroups/otherModelGroups";
import { TemplateBuilderModelGroups } from "../../../type-guards/modelGroups/templateBuilderModelGroups";
import { PresentationItemColumnTypes } from "./PresentationModelEditor/types";

export const getEmptyPresentationTemplateItem = (
  items: NumberIndexedDictionary<AllPresentationTemplateItems>,
  id: number,
  type: TemplateType,
  system: TemplateSystem,
  columnOrder: number
) =>
  produce(presentationTemplateItemPLACEHOLDER, (draft) => {
    draft.aggregationGroupId = null;
    draft.id = id;
    draft.columnOrder = columnOrder;

    if (type === "item" || type === "overview") {
      type OrderableItem =
        PresentationTemplateGroups["isRepresentedAsATable"]["items"][number];
      (draft as OrderableItem).sortOrder = 0; // TODO: Type-guard na itemy
      (draft as OrderableItem).sortType = "empty"; // TODO: Type-guard na itemy
    }
  });

export const getDictionaryOfDataTypesForPresentationItems = (
  templateBuilder: TemplateBuilderModelGroups["hasPresentationModel"]
) => {
  const { presentationTemplate, dataTemplate, tables } = templateBuilder;

  const presentationItems = Object.values(presentationTemplate.items);
  const dataItems = dataTemplate.items;

  return Object.values(presentationItems).reduce((accumulator, { id }) => {
    const { displayColumn: columnName, tableId, sqlType } = dataItems[id];
    const table = tables[tableId];

    const tableName = table.name;

    if (columnName === null) {
      accumulator[id] = null;
      return accumulator;
    }

    let editedColumnName = columnName;

    if (columnName.includes(tableName)) {
      editedColumnName = editedColumnName.replace(`${tableName}_`, "");
    }

    const column = table.columns[editedColumnName];

    if (!column) {
      if (sqlType === "System.Decimal") {
        accumulator[id] = TableColumnInputType.Decimal;
      } else if (sqlType === "System.Int32" || sqlType === "System.Int64") {
        accumulator[id] = TableColumnInputType.Number;
      } else {
        accumulator[id] = TableColumnInputType.Text;
      }

      return accumulator;
    }

    accumulator[id] = column.inputTypeUnsafe;

    return accumulator;
  }, {} as PresentationItemColumnTypes);
};

const assignIdsToSelectItems = (items: SelectItem[]) =>
  items.map((item) => {
    if (item.id) return item;

    return { ...item, id: getShortUuid() };
  });

const transformFunctionTranslations = (
  functions: Record<number, FunctionTemplate>
) =>
  produce(functions, (draft) => {
    Object.entries(functions).forEach(([key, value]) => {
      draft[Number(key)].aliasTranslations =
        value.aliasTranslations ?? EMPTY_TRANSLATIONS;
      draft[Number(key)].tooltipTranslations =
        value.tooltipTranslations ?? EMPTY_TRANSLATIONS;
      draft[Number(key)].mobileAliasTranslations =
        value.mobileAliasTranslations ?? EMPTY_TRANSLATIONS;
    });
  });
const initializePresentationTemplateItems = (
  items: NumberIndexedDictionary<AllPresentationTemplateItems>
) =>
  produce(items, (draft) => {
    Object.entries(draft).forEach(([key, item]) => {
      if (item.selectItems) {
        draft[Number(key)].selectItems = assignIdsToSelectItems(
          item.selectItems
        );
      }
      if (item.columnFormat === null) {
        draft[Number(key)].columnFormat = columnFormatPLACEHOLDER;
      }
      if (item.columnFormatMobile === null) {
        draft[Number(key)].columnFormatMobile = columnFormatPLACEHOLDER;
      }

      if (!item.translations) {
        draft[Number(key)].translations = EMPTY_TRANSLATIONS;
      }
    });
  });

const transformLayoutEntityTypeToLowercase = (
  layout: MobileRowLayoutDTO
): MobileRowLayoutDTO => {
  const updatedLayout = produce(layout.layout, (draft) => {
    Object.keys(draft)
      .map(Number)
      .forEach((key) => {
        draft[key] = {
          ...draft[key],
          type: draft[key].type.toLowerCase() as TypeEnum,
        };
      });
  });
  return {
    ...layout,
    layout: updatedLayout,
  };
};

export const modifyPresentationTemplate = (
  presentationTemplate: AllPresentationTemplates
) =>
  produce(presentationTemplate, (draft) => {
    const { hasPresentationModel, isMultiDetail } =
      typeGuards.forPresentationTemplate;

    if (hasPresentationModel(draft)) {
      draft.items = initializePresentationTemplateItems(draft.items ?? {});
      draft.functionTemplates = transformFunctionTranslations(
        draft.functionTemplates ?? {}
      );
    }

    if (isMultiDetail(draft) && !draft.detailTemplatesOrder) {
      draft.detailTemplatesOrder = {};
    }
  });

export const modifyDataTemplate = (dataTemplate: AllDataTemplates) =>
  produce(dataTemplate, (draft) => {
    // jen kvuli lokalnimu testovani, bude odstraneno az bude implementovana BE cast
    if (typeGuards.forDataTemplate.isDetail(draft)) {
      if (!draft.orderByItemTemplateId) draft.orderByItemTemplateId = {};
      if (!draft.whereItemsByItemTemplateId)
        draft.whereItemsByItemTemplateId = {};
    }

    if (!draft.aggregationGroups) {
      draft.aggregationGroups = [];
    }

    if (!typeGuards.forDataTemplate.isMultiDetail(draft)) {
      return;
    }

    if (!draft.detailTemplates) {
      draft.detailTemplates = [];
    }
  });

export const getKeyColumn = (columns: TableColumns) =>
  Object.values(columns).find((column) => column.isKey);

export const getSaveButtonTooltipTitle = (
  displayTooltip: boolean,
  formatMsg: FormatMsg
): string => {
  if (!displayTooltip) return "";
  return formatMsg("TBValidation.preventedSubmit");
};
